import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import '@musinsa/pbo-core/tokens.css'
import '@musinsa/pbo-core/styles.css'
import '@musinsa/pbo-core/fonts.css'
import './productManagement.css'
import { useIsMutating, useQuery } from '@tanstack/react-query'
import { ConfigProvider } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import request from '@/utils/request'
import { usePboQueryErrorToast } from '@/hooks/usePboQueryErrorToast'
import { invalidError, notice } from '@/components/pbo/notice'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import ProductManagementPageView, { type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { ProductManagementParams, ProductManagementRow, ShipperInfoMap, useGetProductManagementList } from './queries/useGetProductManagementList'
import { downloadProductsExcel, SaveTarget, useDeleteProducts, useSaveProducts } from './queries/useModifyProducts'
import { fromSearchQuery, toDetailedReason } from './queries/searchQuery'
import { parseProductExcel } from './components/excelImport'
import { DEFAULT_PRINT_SPEC, PRINT_SPEC_OPTIONS, openPrintWindow } from './components/barcodePrint'
import { filterHelperRows } from '@/utils/filterHelperRows'
import {
  EMPTY_PRODUCT_ROW,
  GOODS_HELPER_CONDITION_REQUIRED_MESSAGE,
  REASON_DETAIL_MIN_LENGTH,
  SERVICE_TYPE_OPTIONS,
  SHIPPER_LOAD_FAILED_MESSAGE,
  SHIPPER_REQUIRED_MESSAGE,
} from './components/consts'
import { HelperKind, HelperOptionParams, useProductHelperOptions } from './queries/useProductHelperOptions'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

// 상품관리 (ma/ma02) — 컴포넌트 모드 배선(문서 51). UI 는 handoff/page.generated.tsx 가 그리고,
// 이 파일은 옛 손코드의 훅·검증·API 를 Wiring 슬롯에 잇는다. 생성본은 재핸드오프 때 통째로 덮인다.

// BE meta.message 우선, 빈 body 응답(게이트웨이/인프라 차단 등)은 HTTP 상태를 함께 노출
const apiErrMessage = (e: unknown) => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message ?? (resp?.status ? `처리에 실패했습니다. (HTTP ${resp.status})` : '처리에 실패했습니다.')
}

// 조회영역 키(생성본) → 2.0 API 파라미터. 다건 입력은 string[] 로 와서 그대로 넘긴다(단건 서버/다건 클라 필터는 searchQuery.ts).
const asList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) return v.length ? v.map(String) : undefined
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}
const toParams = (f: Record<string, unknown>): ProductManagementParams => ({
  serviceTypes: asList(f.service_type),
  shipperCodes: asList(f.shipper_name),
  brandNames: asList(f.BRD_NM),
  styleNumbers: asList(f.STL_NO),
  itemCodes: asList(f.GDS_CD),
  barcodes: asList(f.BARCODE),
  optionCodes: asList(f.option_code),
})
/** 아직 저장되지 않은 신규 행 — 표 key 는 배선이 쥔다(인덱스면 맨 위 삽입에 체크가 밀린다). */
type NewRow = SaveTarget & { __key: string; __new?: boolean }

/** 폼 값(Record<string, unknown>)을 안전하게 문자열로 — 객체가 '[object Object]' 로 새지 않게 한다. */
const asText = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
/** 화주 축이 있는가 — 유형·명 중 하나면 된다. 없으면 전 화주 상품이 브라우저에 실린다(PR #728 리뷰 지적). */
const hasShipperFilter = (p: ProductManagementParams) => (p.serviceTypes?.length ?? 0) > 0 || (p.shipperCodes?.length ?? 0) > 0
/** 빈 조건(초기 마운트·리셋)에서는 조회하지 않는다 — 조건 없는 전체 조회 방지 */
const hasAnyCondition = (p: ProductManagementParams) =>
  Object.entries(p).some(([k, v]) => k !== '_seq' && (Array.isArray(v) ? v.length > 0 : v != null))

/** 검색 모달의 화주 축 — 조회영역 **지금 값**(화주명은 단일 선택 Select 라 [0] 하나다). 화주명이 없을 때만 유형을 싣는다. */
type HelperShipperAxis = Pick<HelperOptionParams, 'shipperCode' | 'serviceTypes'>
const toHelperShipperAxis = (f?: Record<string, unknown>): HelperShipperAxis => {
  const shipperCode = asList(f?.shipper_name)?.[0]
  if (shipperCode) return { shipperCode }
  const serviceTypes = asList(f?.service_type)?.filter(Boolean)
  return serviceTypes?.length ? { serviceTypes } : {}
}

/**
 * 검색 모달 후보 — 모달 [조회](q.filters.__applied) 때만 받는다. 조건이 모자라면 받지 않고 [조회] 순간 알린다.
 *  뷰가 [조회]마다 새 적용 조건 객체를 주므로(page.generated.tsx stApplied_*) filters 가 바뀔 때 한 번 판정한다.
 *  conditionRequired — goods-codes 는 무페이징이라 대형 화주를 조건 없이 받으면 응답이 수십 MB 다(재고 화면 선례).
 */
/** 모달 적용 조건 객체 → [조회] 순번. 같은 객체면 언제 물어도 같은 번호다(렌더를 다시 해도 요청이 새로 나가지 않는다). */
const applySeqs = new WeakMap<object, number>()
let lastApplySeq = 0
const applySeqOf = (filters?: Record<string, unknown>): number => {
  if (!filters) return 0
  if (!applySeqs.has(filters)) applySeqs.set(filters, (lastApplySeq += 1))
  return applySeqs.get(filters) as number
}

const useHelperRows = (
  kind: HelperKind,
  filters: Record<string, unknown> | undefined,
  axis: HelperShipperAxis,
  conditionRequired = false,
): { rows: Row[]; loading: boolean } => {
  const applied = Boolean(filters?.__applied)
  const params: HelperOptionParams = {
    ...axis,
    brandName: asText(filters?.BRD_NM).trim(),
    itemName: asText(filters?.GDS_NM).trim(),
    styleNumber: asText(filters?.STL_NO).trim(),
  }
  const blocked =
    !axis.shipperCode && !axis.serviceTypes?.length
      ? SHIPPER_REQUIRED_MESSAGE
      : conditionRequired && !params.brandName && !params.itemName
        ? GOODS_HELPER_CONDITION_REQUIRED_MESSAGE
        : undefined
  // 같은 조건으로 [조회]를 다시 눌러도 다시 받는다(1.0 동작 · 본 표 _seq 와 같은 이유) — 적용 조건 객체가 바뀔 때만 번호가 오른다
  const appliedSeq = applySeqOf(filters)
  const helperQuery = useProductHelperOptions(kind, params, appliedSeq, applied && !blocked)
  const { data, isFetching } = helperQuery
  usePboQueryErrorToast(helperQuery) // 검색 모달 후보 조회 실패도 옛 전역 알럿 대신 새 디자인 알럿(notice.failure)
  useEffect(() => {
    if (applied && blocked) notice.invalid(blocked) // 검증(화주·조건 필수) — 토스트
    // eslint-disable-next-line react-hooks/exhaustive-deps -- [조회]를 누른 순간(filters 객체가 바뀔 때)만 알린다
  }, [filters])
  return { rows: applied && !blocked ? (data ?? []) : [], loading: isFetching }
}

function ProductManagement() {
  // ── 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(ST09·GL11 준용).
  // eslint-disable-next-line @typescript-eslint/unbound-method -- 컨텍스트가 주는 안정 참조(내부에서 this 를 쓰지 않는다)
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // 마운트 시 한 번만 복원한다 — 이후 조회로 URL 이 바뀌어도 입력을 덮어쓰지 않는다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restoredParams = useMemo(() => fromSearchQuery(parsedQs), [])

  const [searchParams, setSearchParams] = useState<ProductManagementParams>({})
  // 조회 버튼은 동일 조건이어도 항상 재조회(1.0 동작) — queryKey 에 시퀀스 포함
  const searchSeq = useRef(0)
  // [추가] 버튼·엑셀 업로드로 들어온 신규 행 — 조회 결과 **위**에 붙여 사용자가 검토·선택·저장한다.
  // (데모: "모달 없이 빈 행을 맨 위에 넣는다" · 1.0 TOOL_ADD·TOOL_IMP 파리티)
  // 행 key 는 인덱스가 아니라 증가 카운터다 — 맨 위 삽입으로 인덱스가 밀리면 체크가 다른 행에 옮겨 붙는다.
  const [appended, setAppended] = useState<NewRow[]>([])
  const appendSeq = useRef(0)
  // 표에서 편집된 행 전체(ROWS_rowsChange) — 저장은 '선택 행'을 여기서 찾아 최신 값으로 보낸다
  const edited = useRef<Map<string, SaveTarget>>(new Map())

  /** 신규 행 한 줄 — 화주코드는 조회 조건의 화주를 따른다(저장 시 POST 필수값). */
  const newRow = useCallback((shipper: string, over: Record<string, unknown> = {}): NewRow => {
    appendSeq.current += 1
    // __new = DS 표 계약 — 사용자가 만든 행은 처음부터 필수 미입력(화주코드)을 빨갛게 표기한다(sy07·ma03 과 같다).
    return { ...EMPTY_PRODUCT_ROW, _id: 0, SPR_NM: shipper, ...over, __key: `new-${appendSeq.current}`, __new: true } as unknown as NewRow
  }, [])

  // ── 화주 옵션 + 화주타입 — 응답에 서비스타입 필드가 없어 타입별 조회를 병렬 합성해 코드→타입 맵을 만든다
  // (화주타입/화주명 표시 컬럼은 상품 조회 응답에 없어 FE 파생 — LLD-9612 응답 범위 밖)
  const shipperQuery = useQuery({
    queryKey: ['MA02_SHIPPER_OPTIONS'],
    meta: { noErrorMessage: true }, // 실패 안내는 아래 usePboQueryErrorToast 의 새 디자인 알럿으로(옛 전역 알럿 대신 — sy07·ma03 과 같은 규약)
    queryFn: async () => {
      const byType = await Promise.all(
        SHIPPER_TYPES.map((type) =>
          request
            .post<
              ApiResponseV2<{ items: { shipperCode: string; shipperName: string }[] }>,
              { logisticsServiceTypes: string[] }
            >('/api/v1/shipper-filter-options', { logisticsServiceTypes: [type] })
            .then((r) => (r.data?.items ?? []).map((s) => ({ ...s, serviceType: type as string }))),
        ),
      )
      return byType.flat()
    },
  })
  const { data: shippers = [] } = shipperQuery
  // 화주 목록 실패 — 옵션이 비면 화주를 못 고른다. 화면은 그리되 이유를 알린다.
  usePboQueryErrorToast(shipperQuery, SHIPPER_LOAD_FAILED_MESSAGE, { preferFallback: true }) // 화면 문구 고정 — BE 문구로 덮이면 셀렉트가 왜 비었는지 모른다
  const shipperOptions: Option[] = useMemo(() => shippers.map((s) => ({ value: s.shipperCode, label: s.shipperName })), [shippers])
  const shipperInfo: ShipperInfoMap = useMemo(
    () => Object.fromEntries(shippers.map((s) => [s.shipperCode, { shipperName: s.shipperName, serviceType: s.serviceType }])),
    [shippers],
  )
  // 기본 화주 = 헤더에서 전환한 화주(shipperCode — 전환마다 갱신). SPR_NM 은 로그인 시점 값이라 전환해도 안 바뀐다.
  // 옵션에 없으면 빈칸 — 첫 화주로 채우지 않는다.
  const user = useMemo(() => AuthStorage.user.get(), [])
  const defaultShipper = useMemo(() => shipperOptions.find((o) => o.value === user.shipperCode)?.value, [shipperOptions, user])

  const saveProducts = useSaveProducts()
  const deleteProducts = useDeleteProducts()
  const { data: combo } = useGetExportCombo({ errorToast: true })
  const reasonOptions: Option[] = useMemo(
    () =>
      (combo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: combo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [combo],
  )

  const onSearch = useCallback(
    (params: ProductManagementParams) => {
      // 마지막 조회 조건을 메뉴 세션·URL 에 남긴다 (_seq 는 저장 대상 아님)
      MenuStorage.menuOnSession.setSearch(params)
      reload()
      replaceQuery(params)
      edited.current.clear()
      setAppended([])
      searchSeq.current += 1
      setSearchParams({ ...params, _seq: searchSeq.current })
    },
    [reload, replaceQuery],
  )

  // 진입 1회 복원 재조회 — 남은 조회 조건(URL/세션)이 있을 때만(ST09·GL11 준용). 최초 진입은 조회하지 않는다.
  const didInit = useRef(false)
  useEffect(() => {
    if (didInit.current || !restoredParams) return
    didInit.current = true
    onSearch(restoredParams)
  }, [onSearch, restoredParams])

  /** 선택 행(생성본 Row) → 저장 대상. 편집 반영분이 있으면 그 값을 쓴다. */
  const toSaveTargets = useCallback(
    (rows: Row[]): SaveTarget[] => rows.map((r) => (edited.current.get(String(r.key)) ?? r) as unknown as SaveTarget),
    [],
  )

  // ⚠️ useMemo 로 감싸지 않는다(가이드 52 항목 10) — 뷰가 렌더 첫머리에 모듈 홀더로 받으므로 매 렌더
  //   새 객체가 맞다. 메모이즈하면 첫 렌더 클로저(searchParams·appended 등)가 남아 표가 갱신되지 않는다.
  // 검색 모달의 화주 축 — 본 표 슬롯(ROWS)이 받는 조회영역 지금 값에서 잡는다. 모달 슬롯(ROWS3~5)의 filters 는
  //  모달 적용 조건이라 화주가 없다. 생성본이 매 렌더 ROWS 를 ROWS2~5 보다 먼저 부른다(page.generated.tsx ROWS_R → ROWS3_R)
  //  — 이 순서가 바뀌면 모달이 한 렌더 늦은 화주를 쓴다. 재핸드오프 때 호출 순서를 확인할 것.
  let helperShipper: HelperShipperAxis = {}
  const wiring: Wiring = {
    permission: usePermissionSlot('ma17'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { ma02Table: coreTableLayoutSlot('MA17_HEAD') },
    rows: {
      // 표 본체 — 조회 훅을 **슬롯 안에서** 부른다.
      //  ⚠️ 훅 규칙 예외처럼 보이지만 안전하다: 생성본(ProductManagementPageView)이 이 슬롯을
      //   **컴포넌트 본문 첫머리에서 조건 없이 매 렌더 같은 순서로** 부른다는 계약이 있다(문서 51 컴포넌트 모드).
      //   그래서 훅 개수·순서가 렌더마다 같다. useBusy 슬롯도 같은 이유로 안전하다.
      //   ✋ 이 계약이 깨지면(생성본이 ROWS 를 조건부로 부르거나 호출 위치가 바뀌면) 훅 순서가 어긋난다 —
      //   재핸드오프로 생성본이 바뀌었을 때 호출부를 먼저 확인할 것. 조회 훅을 컴포넌트 본문으로 끌어올리면
      //   searchParams 가 바뀔 때마다 wiring 전체가 새로 만들어져 표가 깜빡인다(그래서 슬롯 안에 둔다).
      //   이 계약은 슬롯 안의 **모든 훅**에 적용된다 — 조회 훅·실패 알럿(usePboQueryErrorToast, 안에서 useEffect)·상한 경고 useEffect.
      //   훅을 더하거나 뺄 땐 조건 없이 같은 순서로 부르는지 확인할 것.
      ROWS: (_demo, q) => {
        helperShipper = toHelperShipperAxis(q?.filters)
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const listQuery = useGetProductManagementList(searchParams, hasAnyCondition(searchParams))
        const { data, isFetching } = listQuery
        // eslint-disable-next-line react-hooks/rules-of-hooks
        usePboQueryErrorToast(listQuery)
        // 페이지 순회 안전 상한(MAX_PAGES) 초과 — 초과분 무경고 누락 금지(명시 경고). 옛 ProductManagementTable 과 같은 문구.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (!data?.truncated) return
          notice.failure(`조회 결과가 상한을 초과하여 ${data.contents.length.toLocaleString()}건만 표시됩니다. 조건을 좁혀 다시 조회하세요.`)
        }, [data])
        // 화주타입/화주명은 렌더마다 현재 화주 목록과 조인한다 — 상품 응답이 화주 목록보다 먼저 와도 뒤늦게 채워진다(14201)
        const base = (data?.contents ?? []).map((r) => ({
          key: `id-${r._id}`,
          ...r,
          service_type: shipperInfo[r.SPR_NM]?.serviceType ?? '',
          shipper_name: shipperInfo[r.SPR_NM]?.shipperName ?? '',
        })) as unknown as Row[]
        const extra = appended.map((r) => ({ key: r.__key, ...r })) as unknown as Row[]
        // 상품 조회 응답(0건 포함)이 있는데 화주 목록이 아직이면 로딩을 유지한다 — 도착하면 행 JSON 이 바뀌어 표가 편집 상태를 원본으로 되돌린다(pbo-core Table).
        //  응답이 없으면(조회 전·[초기화] 후) 조인할 행이 없어 스피너를 띄우지 않는다.
        return { rows: [...extra, ...base], total: base.length + extra.length, loading: isFetching || (data != null && shipperQuery.isLoading) }
      },
      // 바코드명 검색 — 여는 버튼이 없다(1.0 은 본 표 바코드명 셀 헬퍼였는데 핸드오프에서 셀 표현이 빠졌다 · HANDOFF-CHANGES.md).
      //  열 길이 없어 API 를 잇지 않고 데모 후보를 적용 조건으로 거르는 채로 둔다 — 셀 헬퍼를 되살릴 때 /products/barcodes 로 잇는다.
      ROWS2: (demo, q) => filterHelperRows(demo, q?.filters, ['BRD_NM', 'GDS_NM']),
      // 검색 모달 3종 — 슬롯 안에서 훅을 부른다(ROWS 와 같은 계약: 생성본이 매 렌더 조건 없이 같은 순서로 부른다).
      // 브랜드명 — 조건 BRD_NM / 컬럼 BRD_NM
      // eslint-disable-next-line react-hooks/rules-of-hooks
      ROWS3: (_demo, q) => useHelperRows('brand-names', q?.filters, helperShipper),
      // 스타일번호 — 조건 BRD_NM·STL_NO(정확일치) / 컬럼 STL_NO·BRD_NM·GDS_CD
      // eslint-disable-next-line react-hooks/rules-of-hooks
      ROWS4: (_demo, q) => useHelperRows('style-numbers', q?.filters, helperShipper),
      // 상품코드 — 조건 BRD_NM·GDS_NM(하나 필수) / 컬럼 GDS_CD·BRD_NM·GDS_NM
      // eslint-disable-next-line react-hooks/rules-of-hooks
      ROWS5: (_demo, q) => useHelperRows('goods-codes', q?.filters, helperShipper, true),
    },
    actions: {
      search: (filters) => {
        const params = toParams(filters)
        // 화주 없이 조회하면 전 화주 상품이 통째로 실린다 — 화면 별표대로 여기서 막는다.
        if (!hasShipperFilter(params)) {
          notice.invalid(SHIPPER_REQUIRED_MESSAGE)
          return false
        }
        onSearch(params)
      },
      // [추가] — 모달 없이 빈 행을 맨 위에 넣는다(1.0 TOOL_ADD 파리티). 저장 시 _isNew 가 POST 로 간다.
      //   ctx.defaults 는 생성본이 주는 열 기본값(지금은 비어 있음) — 앞으로 채워져도 그대로 반영된다.
      ma02AddButton: (ctx) => {
        const shipper = searchParams.shipperCodes?.[0] ?? (defaultShipper ? String(defaultShipper) : '')
        setAppended((prev) => [newRow(shipper, ctx.defaults), ...prev])
      },
      // 편집된 행 전체 — 저장 때 선택 행의 최신 값을 여기서 찾는다
      ROWS_rowsChange: (rows) => {
        rows.forEach((r) => edited.current.set(String((r as Row).key), r as unknown as SaveTarget))
      },
      // [MOMS 공통] 저장 후 자동 재조회·조회조건 리셋 금지 — 편집분만 반영하고 조회조건/스크롤/선택을 유지한다.
      // 부분 실패 대응: 성공 행은 _id/_isNew 즉시 확정(재저장 시 POST 중복 생성 방지), 실패 행만 사유와 함께 표시한다.
      ma02SaveButton: async (ctx) => {
        const targets = toSaveTargets(ctx.selected)
        if (!targets.length) return false
        const results = await saveProducts.mutateAsync(targets) // 행별 결과 수집 — throw 하지 않음
        const failed = results.filter((r) => !r.ok)
        const succeeded = results.filter((r) => r.ok)
        // 성공 행 확정 — 신규 행(POST)은 응답 itemId 로 _id 갱신, 이후 재저장은 PUT
        succeeded.forEach((r) => {
          const row = targets[r.index]
          edited.current.set(String((row as unknown as Row).key), { ...row, _id: r.id || row._id, _isNew: false })
        })
        if (succeeded.some((r) => targets[r.index]._isNew)) {
          // 신규 행이 확정되면 목록을 다시 받아 서버 값(옵션코드·감사 필드)으로 맞춘다
          setAppended([])
          searchSeq.current += 1
          setSearchParams((p) => ({ ...p, _seq: searchSeq.current }))
        }
        if (!failed.length) return // 성공 토스트는 생성본이 낸다
        if (succeeded.length) notice.success(`${succeeded.length}건 저장 완료`)
        // 행별 실패 — 알럿 한 장에 줄로 모은다(행마다 알럿을 쌓지 않는다). 각 줄 문구는 옛 토스트와 같다.
        notice.failure(
          failed
            .map((r) => {
              const row = targets[r.index]
              const label = row.BARCODE || row.GDS_NM || `${r.index + 1}번째 행`
              return `저장 실패 — ${label}: ${apiErrMessage(r.error)}`
            })
            .join('\n'),
        )
        // 실패가 있으면 throw — 생성본이 선택을 풀지 않아 사용자가 고쳐 재저장할 수 있다
        throw new Error(`${failed.length}건 저장 실패`)
      },
      // 삭제 확인창 — DELETE /{itemId}. 미저장 신규 행은 로컬 제거만(1.0 TOOL_RMV 파리티)
      ma02DeleteConfirm_ok: async (ctx) => {
        const targets = toSaveTargets(ctx.selected)
        if (!targets.length) return false
        try {
          await deleteProducts.mutateAsync(targets)
        } catch (e) {
          notice.failure(apiErrMessage(e))
          throw e // 확인창을 열어 둔다
        }
        const newKeys = new Set(targets.filter((t) => t._isNew).map((t) => String((t as unknown as Row).key)))
        if (newKeys.size) setAppended((prev) => prev.filter((r) => !newKeys.has(r.__key)))
        targets.forEach((t) => edited.current.delete(String((t as unknown as Row).key)))
        searchSeq.current += 1
        setSearchParams((p) => ({ ...p, _seq: searchSeq.current }))
      },
      // 1.0 파리티 — 다운로드는 선택 무관 필터 전체(서버 xlsx, EXPORT 감사). 사유는 감사로그 remark 에 실린다.
      ma02DownloadModal_ok: async (ctx) => {
        const reasonCode = asText(ctx.form?.reason)
        const reasonDetail = asText(ctx.form?.detail).trim()
        // 옛 모달의 [확인] 잠금을 슬롯에서 되살린다 — 뷰에는 잠금 슬롯이 없다(가이드 52 §5-A ⑥)
        //  검증 실패는 안내 뒤 throw(모달 유지) — 생성본은 거부를 조용히 삼키므로 안내는 슬롯이 낸다.
        if (!reasonCode) throw invalidError('다운로드 사유를 선택해 주세요.')
        if (reasonDetail.length < REASON_DETAIL_MIN_LENGTH) {
          throw invalidError(`상세 사유를 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`)
        }
        try {
          await downloadProductsExcel(searchParams, toDetailedReason({ reasonCode, reasonDetail }))
        } catch (e) {
          notice.failure(apiErrMessage(e))
          throw e
        }
      },
      // 선택 행의 바코드 라벨을 인쇄 창으로(1.0 PRINT 파리티).
      ma02BarcodePrintModal_ok: (ctx) => {
        const rows = toSaveTargets(ctx.selected) as unknown as ProductManagementRow[]
        const spec = asText(ctx.form?.spec) || DEFAULT_PRINT_SPEC
        const copies = Number(ctx.form?.copies ?? 1) || 1
        if (!rows.some((r) => r.BARCODE)) throw invalidError('바코드가 있는 행을 선택해 주세요.')
        openPrintWindow(rows, spec, copies)
      },
      // 1.0 TOOL_IMP 파리티 — 업로드는 **클라 파싱** 후 신규 행으로 반영하고, 사용자가 검토·선택·[저장]한다(서버 업로드 아님).
      ma02UploadModal_ok: async (ctx) => {
        const picked = (ctx.form?.file ?? (ctx.form?.files as unknown[] | undefined)?.[0]) as File | undefined
        if (!picked) throw invalidError('엑셀 파일을 선택해 주세요.')
        let imported
        try {
          imported = await parseProductExcel(picked)
        } catch (e) {
          throw invalidError(e instanceof Error ? e.message : '엑셀 파일을 읽을 수 없습니다.')
        }
        if (!imported.length) throw invalidError('가져올 데이터 행이 없습니다.')
        const shipper = searchParams.shipperCodes?.[0] ?? (defaultShipper ? String(defaultShipper) : '')
        setAppended((prev) => [...imported.map((item) => newRow(shipper, item)), ...prev])
        notice.success(`${imported.length}행을 가져왔습니다 — 검토 후 선택·저장하세요.`)
      },
      // 검색 모달 4종의 [선택] — 고른 값을 조회영역 칸에 넣는 것은 생성본이 한다(브랜드·스타일·상품코드). 배선은 통과만 시킨다.
      ma02BarcodeHelperModal_ok: () => {},
      ma02BrdSearchModal_ok: () => {},
      ma02StlSearchModal_ok: () => {},
      ma02GdsSearchModal_ok: () => {},
    },
    // 모달 열기 전 가드 — 선택이 필요한 것만 막는다(옛 requireSelect disabled 자리)
    opens: {
      ma02DeleteButton: (ctx) => ctx.selected.length > 0,
      ma02PrintButton: (ctx) => ctx.selected.length > 0,
    },
    // 옵션 — 데모 목데이터 대신 API/상수. 화주명은 **value = 화주코드**라 조회 조건도 코드로 나간다.
    options: {
      MA02SHIPPER_TYPE_OPTIONS: () => SERVICE_TYPE_OPTIONS,
      MA02SHIPPER_NAME_OPTIONS: () => shipperOptions,
      // 콤보가 오기 전·빈 응답이면 고를 사유가 없다 — 데모 사유는 코드가 아니라 BE 로 보내면 안 된다. 빈 채로 확인하면 action 이 막는다.
      REASONRADIO_OPTIONS: () => reasonOptions,
      SPECSELECT_OPTIONS: () => PRINT_SPEC_OPTIONS,
    },
    // 조회 기본값 — 비워 둔다. 생성본이 **마운트 1회**만 계산해(useMemo([])) 화주 옵션을 확인할 수 없고,
    // 여기서 넣은 값은 filterFallback 이 덮지 못한다(옵션에 없는 화주가 칸에 남아 조회된다). 화주는 filterFallback 이 채운다.
    // 데모 기본값이 생기더라도 새지 않게 슬롯은 남긴다.
    filterDefaults: {
      ma02FilterCard: () => ({}),
    },
    // 진입 복원 — URL/세션에 남은 마지막 조회 조건을 기본값 위에 얹는다
    filterInitial: {
      ma02FilterCard: (defaults) =>
        restoredParams
          ? {
              ...defaults,
              service_type: restoredParams.serviceTypes,
              shipper_name: restoredParams.shipperCodes,
              BRD_NM: restoredParams.brandNames,
              STL_NO: restoredParams.styleNumbers,
              GDS_CD: restoredParams.itemCodes,
              BARCODE: restoredParams.barcodes,
              option_code: restoredParams.optionCodes,
            }
          : defaults,
    },
    // 기본 화주 — 화주 옵션이 도착한 뒤(·[초기화] 뒤) 조회 조건이 비어 있으면 전환 화주로 채운다(값이 있는 키는 생성본이 덮지 않는다).
    // 첫 화주로 폴백하지 않는다 — 전환 화주가 옵션에 있을 때만 채운다(defaultShipper 계산과 같다).
    filterFallback: {
      ma02FilterCard: (current, options) => {
        if (asList(current.shipper_name) || asList(current.service_type)) return undefined
        const opts = options.MA02SHIPPER_NAME_OPTIONS ?? []
        const pick = opts.find((o) => o.value === user.shipperCode)?.value
        return pick ? { shipper_name: [String(pick)] } : undefined
      },
    },
    // 초기화 — 저장된 조건도 함께 비워 다음 진입이 기본값으로 열리게 한다(ST09 준용)
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      replaceQuery({})
      edited.current.clear()
      setAppended([])
      setSearchParams({})
    },
    // 요청 중이면 선택 게이트 버튼(저장·삭제)을 잠근다 — 중복 요청 방지(모달을 닫아서 막지 않는다)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useBusy: () => useIsMutating() > 0,
  }

  return (
    <ConfigProvider theme={pboLogisticsTheme}>
      <ProductManagementPageView wiring={wiring} />
    </ConfigProvider>
  )
}

export default ProductManagement
